import { type ReactNode } from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

import { HeroBackground } from '@/components/hero-background';
import { Button } from '@/components/ui/button';
import { useLocale } from '@/i18n/locale-context';

function Fade({
  children,
  delayMs,
  className,
}: {
  children: ReactNode;
  delayMs: number;
  className?: string;
}) {
  const shouldReduceMotion = useReducedMotion();

  return (
    <motion.div
      className={className}
      initial={shouldReduceMotion ? false : { opacity: 0, y: 18 }}
      animate={shouldReduceMotion ? undefined : { opacity: 1, y: 0 }}
      transition={{ duration: 0.58, delay: delayMs / 1000, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.div>
  );
}

function scrollTo(id: string) {
  document.getElementById(id)?.scrollIntoView({
    behavior: 'smooth',
    block: 'nearest',
  });
}

export function Hero() {
  const { t, locale } = useLocale();

  // 标题关键词 primary 高亮（en 高亮 "Think"，zh 高亮 "所思"）
  const keyword = locale === 'zh-CN' ? '所思' : 'Think';
  const keywordIndex = t.hero.brandTitle.indexOf(keyword);
  const before = keywordIndex > 0 ? t.hero.brandTitle.slice(0, keywordIndex) : '';
  const after =
    keywordIndex >= 0 ? t.hero.brandTitle.slice(keywordIndex + keyword.length) : '';
  const hasKeyword = t.hero.brandTitle.includes(keyword);

  return (
    <section id="workspace" className="relative flex min-h-[100dvh] scroll-mt-24 flex-col">
      <HeroBackground />

      <div className="relative z-10 mx-auto flex w-full max-w-6xl flex-1 items-center px-6 pb-16 pt-28 sm:px-10 sm:pt-36">
        <div className="mx-auto max-w-3xl text-center">
          <Fade delayMs={80}>
            <h1 className="flex flex-col items-center gap-4">
              <span className="hero-title text-balance text-4xl font-normal leading-tight tracking-tight text-foreground sm:text-5xl md:text-6xl lg:text-7xl lg:leading-tight">
                {before}
                {hasKeyword ? (
                  <span className="text-primary">{keyword}</span>
                ) : (
                  t.hero.brandTitle
                )}
                {after}
              </span>
              <span className="mx-auto block max-w-none text-lg leading-relaxed text-foreground/70 sm:whitespace-nowrap sm:text-xl">
                {t.hero.subtitle}
              </span>
            </h1>
          </Fade>

          <Fade delayMs={130} className="mt-6 flex flex-wrap items-center justify-center gap-2 sm:gap-3">
            {t.hero.featureTags.map((tag) => (
              <span
                key={tag}
                className="rounded-full border border-primary/20 bg-primary/10 px-3 py-1 text-xs tracking-wide text-foreground/80"
              >
                {tag}
              </span>
            ))}
          </Fade>

          <Fade delayMs={160} className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <Button
              type="button"
              size="lg"
              onClick={() => scrollTo('download')}
              className="rounded-full px-8 shadow-[0_0_30px_rgba(139,132,226,0.25)] transition-shadow duration-300 hover:shadow-[0_0_45px_rgba(139,132,226,0.45)]"
            >
              {t.hero.downloadCta}
              <ArrowRight className="ml-1.5 h-4 w-4" aria-hidden />
            </Button>
            <Button
              type="button"
              size="lg"
              variant="outline"
              onClick={() => scrollTo('capabilities')}
              className="rounded-full px-8"
            >
              {t.hero.exploreCta}
            </Button>
          </Fade>
        </div>
      </div>
    </section>
  );
}
